import classNames from 'classnames';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import React from 'react';
import MediaQuery from 'react-responsive';

import {changeBrushSize} from '../../reducers/brush-mode';
import {changeBrushSize as changeEraserSize} from '../../reducers/eraser-mode';
import {changeBitBrushSize} from '../../reducers/bit-brush-size';
import {changeBitEraserSize} from '../../reducers/bit-eraser-size';
import {setShapesFilled} from '../../reducers/fill-bitmap-shapes';
import {
    changePolygonSides,
    changePolygonRadius,
    MAX_POLYGON_SIDES,
    MIN_POLYGON_SIDES
} from '../../reducers/polygon-mode';
import {changeTextAlignment} from '../../reducers/text-alignment';

import FontDropdown from '../../containers/font-dropdown.jsx';
import LiveInputHOC from '../forms/live-input-hoc.jsx';
import Label from '../forms/label.jsx';
import {defineMessages, injectIntl, intlShape} from 'react-intl';
import Input from '../forms/input.jsx';
import InputGroup from '../input-group/input-group.jsx';
import LabeledIconButton from '../labeled-icon-button/labeled-icon-button.jsx';
import Modes from '../../lib/modes';
import Formats, {isBitmap, isVector} from '../../lib/format';
import {hideLabel} from '../../lib/hide-label';
import styles from './mode-tools.css';
import layout from '../../lib/layout-constants';
import Dropdown from '../dropdown/dropdown.jsx';
import Button from '../button/button.jsx';
import TWRenderRecoloredImage from '../../tw-recolor/render.jsx';
import {BLEND_MODES, MIXED_BLEND_MODE, getBlendModeName} from '../../lib/blend-modes';

import copyIcon from '!../../tw-recolor/build!./icons/copy.svg';
import cutIcon from '!../../tw-recolor/build!./icons/cut.svg';
import pasteIcon from '!../../tw-recolor/build!./icons/paste.svg';
import deleteIcon from '!../../tw-recolor/build!./icons/delete.svg';

import bitBrushIcon from '../bit-brush-mode/brush.svg';
import bitEraserIcon from '../bit-eraser-mode/eraser.svg';
import bitLineIcon from '../bit-line-mode/line.svg';
import brushIcon from '../brush-mode/brush.svg';
import curvedPointIcon from '!../../tw-recolor/build!./icons/curved-point.svg';
import eraserIcon from '../eraser-mode/eraser.svg';
import flipHorizontalIcon from '!../../tw-recolor/build!./icons/flip-horizontal.svg';
import flipVerticalIcon from '!../../tw-recolor/build!./icons/flip-vertical.svg';
import centerIcon from '!../../tw-recolor/build!./icons/center.svg';
import straightPointIcon from '!../../tw-recolor/build!./icons/straight-point.svg';
import maskIcon from '!../../tw-recolor/build!./icons/mask.svg';
import subtractIcon from '!../../tw-recolor/build!./icons/subtract.svg';
import filterIcon from '!../../tw-recolor/build!./icons/filter.svg';
import mergeIcon from '!../../tw-recolor/build!./icons/merge.svg';
import roundedCornerIcon from './icons/rounded-corner.svg';
import pentagonIcon from './icons/pentagon.svg';
import bitOvalIcon from '../bit-oval-mode/oval.svg';
import bitRectIcon from '../bit-rect-mode/rectangle.svg';
import bitOvalOutlinedIcon from '../bit-oval-mode/oval-outlined.svg';
import bitRectOutlinedIcon from '../bit-rect-mode/rectangle-outlined.svg';
import alignLeftIcon from './icons/align-left.svg';
import alignCenterIcon from './icons/align-center.svg';
import alignRightIcon from './icons/align-right.svg';
import alignJustifyIcon from './icons/align-justify.svg';
import effectsColorIcon from './icons/effects-color.svg';
import effectsHueShiftIcon from '!../../tw-recolor/build!./icons/effects-hueShift.svg';
import effectsBrightnessIcon from '!../../tw-recolor/build!./icons/effects-brightness.svg';
import effectsSaturateIcon from '!../../tw-recolor/build!./icons/effects-saturate.svg';
import effectsOpacityIcon from '!../../tw-recolor/build!./icons/effects-opacity.svg';
import effectsPosterizeIcon from '!../../tw-recolor/build!./icons/effects-posterize.svg';

import {MAX_STROKE_WIDTH} from '../../reducers/stroke-width';

const LiveInput = LiveInputHOC(Input);
const ModeToolsComponent = props => {
    const messages = defineMessages({
        brushSize: {
            defaultMessage: 'Size',
            description: 'Label for the brush size input',
            id: 'paint.modeTools.brushSize'
        },
        eraserSize: {
            defaultMessage: 'Eraser size',
            description: 'Label for the eraser size input',
            id: 'paint.modeTools.eraserSize'
        },
        copy: {
            defaultMessage: 'Copy',
            description: 'Label for the copy button',
            id: 'paint.modeTools.copy'
        },
        cut: {
            defaultMessage: 'Cut',
            description: 'Label for the cut button',
            id: 'paint.modeTools.cut'
        },
        paste: {
            defaultMessage: 'Paste',
            description: 'Label for the paste button',
            id: 'paint.modeTools.paste'
        },
        delete: {
            defaultMessage: 'Delete',
            description: 'Label for the delete button',
            id: 'paint.modeTools.delete'
        },
        curved: {
            defaultMessage: 'Curved',
            description: 'Label for the button that converts selected points to curves',
            id: 'paint.modeTools.curved'
        },
        pointed: {
            defaultMessage: 'Pointed',
            description: 'Label for the button that converts selected points to sharp points',
            id: 'paint.modeTools.pointed'
        },
        thickness: {
            defaultMessage: 'Thickness',
            description: 'Label for the number input to choose the line thickness',
            id: 'paint.modeTools.thickness'
        },
        flipHorizontal: {
            defaultMessage: 'Flip Horizontal',
            description: 'Label for the button to flip the image horizontally',
            id: 'paint.modeTools.flipHorizontal'
        },
        flipVertical: {
            defaultMessage: 'Flip Vertical',
            description: 'Label for the button to flip the image vertically',
            id: 'paint.modeTools.flipVertical'
        },
        center: {
            defaultMessage: 'Center',
            description: 'Label for the button that moves the selected objects to the center of the canvas',
            id: 'paint.modeTools.center'
        },
        filled: {
            defaultMessage: 'Filled',
            description: 'Label for the button that sets the bitmap rectangle/oval mode to draw outlines',
            id: 'paint.modeTools.filled'
        },
        outlined: {
            defaultMessage: 'Outlined',
            description: 'Label for the button that sets the bitmap rectangle/oval mode to draw filled-in shapes',
            id: 'paint.modeTools.outlined'
        },
        polygonRadius: {
            defaultMessage: 'Corner radius',
            description: 'Label for the polygon corner radius input',
            id: 'paint.modeTools.polygonRadius'
        },
        polygonSides: {
            defaultMessage: 'Sides',
            description: 'Label for the polygon side count input',
            id: 'paint.modeTools.polygonSides'
        },
        mask: {
            defaultMessage: 'Mask',
            description: 'Label for the `Mask intersections` button',
            id: 'paint.paintEditor.mask'
        },
        subtract: {
            defaultMessage: 'Subtract',
            description: 'Label for the `Subtract intersections` button',
            id: 'paint.paintEditor.subtract'
        },
        filter: {
            defaultMessage: 'Filter',
            description: 'Label for the `Filter intersections` button',
            id: 'paint.paintEditor.filter'
        },
        merge: {
            defaultMessage: 'Merge',
            description: 'Label for the `Merge objects` button',
            id: 'paint.paintEditor.merge'
        },
        masktools: {
            defaultMessage: 'Booleans',
            description: 'Label for dropdown to access the masking tools',
            id: 'paint.paintEditor.masktools'
        },
        more: {
            defaultMessage: 'More',
            description: 'Label for dropdown to access more action buttons',
            id: 'paint.paintEditor.more'
        },
        alignLeft: {
            defaultMessage: 'Left',
            description: 'Label for the text align left button',
            id: 'paint.modeTools.alignLeft'
        },
        alignCenter: {
            defaultMessage: 'Center',
            description: 'Label for the text align center button',
            id: 'paint.modeTools.alignCenter'
        },
        alignRight: {
            defaultMessage: 'Right',
            description: 'Label for the text align right button',
            id: 'paint.modeTools.alignRight'
        },
        alignJustify: {
            defaultMessage: 'Justify',
            description: 'Label for the text justify button',
            id: 'paint.modeTools.alignJustify'
        },
        effectsColor: {
            defaultMessage: 'Color',
            description: 'Label for dropdown for the color effect',
            id: 'paint.paintEditor.effectsColor'
        },
        effectsHueShift: {
            defaultMessage: 'Hue Shift',
            description: 'Label for dropdown for the hue shift effect',
            id: 'paint.paintEditor.effectsHueShift'
        },
        effectsBrightness: {
            defaultMessage: 'Brightness',
            description: 'Label for dropdown for the brightness effect',
            id: 'paint.paintEditor.effectsBrightness'
        },
        effectsSaturate: {
            defaultMessage: 'Saturate',
            description: 'Label for dropdown for the saturate effect',
            id: 'paint.paintEditor.effectsSaturate'
        },
        effectsOpacity: {
            defaultMessage: 'Opacity',
            description: 'Label for dropdown for the opacity effect',
            id: 'paint.paintEditor.effectsOpacity'
        },
        effectsPosterize: {
            defaultMessage: 'Posterize',
            description: 'Label for dropdown for the posterize effect',
            id: 'paint.paintEditor.effectsPosterize'
        },
        blendMode: {
            defaultMessage: 'Blend',
            description: 'Label for the dropdown that sets a shape\'s blending mode',
            id: 'paint.modeTools.blendMode'
        },
        blendMixed: {
            defaultMessage: 'Mixed',
            description: 'Shown in the blend mode dropdown when selected shapes have different blend modes',
            id: 'paint.modeTools.blendMixed'
        }
    });

    const EffectOption = ({id, message, icon}) => (
        <Button
            className={styles.modMenuItem}
            // eslint-disable-next-line react/jsx-no-bind
            onClick={() => props.onApplyEffect(id)}
        >
            <TWRenderRecoloredImage
                className={styles.menuItemIcon}
                draggable={false}
                src={icon}
            />
            <span>{messages[`effects${message}`] ?
                props.intl.formatMessage(messages[`effects${message}`]) :
                message}</span>
        </Button>
    );
    EffectOption.propTypes = {
        id: PropTypes.string.isRequired,
        message: PropTypes.string.isRequired,
        icon: PropTypes.string.isRequired
    };

    const blendModeControl = (
        <InputGroup
            className={styles.modDashedBorder}
            disabled={!props.hasSelection}
        >
            <Label text={props.intl.formatMessage(messages.blendMode)}>
                <Dropdown
                    className={classNames(styles.modUnselect, styles.blendModeDropdown)}
                    enterExitTransitionDurationMs={20}
                    popoverContent={
                        <InputGroup className={styles.modContextMenu}>
                            {BLEND_MODES.map(mode => (
                                <Button
                                    key={mode}
                                    className={styles.modMenuItem}
                                    highlighted={props.blendMode === mode}
                                    // eslint-disable-next-line react/jsx-no-bind
                                    onClick={() => props.onChangeBlendMode(mode)}
                                >
                                    <span>{getBlendModeName(mode)}</span>
                                </Button>
                            ))}
                        </InputGroup>
                    }
                    tipSize={.01}
                >
                    <span>
                        {props.blendMode === MIXED_BLEND_MODE ?
                            props.intl.formatMessage(messages.blendMixed) :
                            getBlendModeName(props.blendMode)
                        }
                    </span>
                </Dropdown>
            </Label>
        </InputGroup>
    );

    switch (props.mode) {
    case Modes.BRUSH:
        /* falls through */
    case Modes.BIT_BRUSH:
        /* falls through */
    case Modes.BIT_LINE:
    {
        const currentIcon = isVector(props.format) ? brushIcon :
            props.mode === Modes.BIT_LINE ? bitLineIcon : bitBrushIcon;
        const currentBrushValue = isBitmap(props.format) ? props.bitBrushSize : props.brushValue;
        const changeFunction = isBitmap(props.format) ? props.onBitBrushSliderChange : props.onBrushSliderChange;
        const currentMessage = props.mode === Modes.BIT_LINE ? messages.thickness : messages.brushSize;
        return (
            <div className={classNames(props.className, styles.modeTools)}>
                <div>
                    <img
                        alt={props.intl.formatMessage(currentMessage)}
                        className={styles.modeToolsIcon}
                        draggable={false}
                        src={currentIcon}
                    />
                </div>
                <LiveInput
                    range
                    small
                    max={MAX_STROKE_WIDTH}
                    min="1"
                    type="number"
                    value={currentBrushValue}
                    onSubmit={changeFunction}
                />
            </div>
        );
    }
    case Modes.BIT_ERASER:
        /* falls through */
    case Modes.ERASER:
    {
        const currentIcon = isVector(props.format) ? eraserIcon : bitEraserIcon;
        const currentEraserValue = isBitmap(props.format) ? props.bitEraserSize : props.eraserValue;
        const changeFunction = isBitmap(props.format) ? props.onBitEraserSliderChange : props.onEraserSliderChange;
        return (
            <div className={classNames(props.className, styles.modeTools)}>
                <div>
                    <img
                        alt={props.intl.formatMessage(messages.eraserSize)}
                        className={styles.modeToolsIcon}
                        draggable={false}
                        src={currentIcon}
                    />
                </div>
                <LiveInput
                    range
                    small
                    max={MAX_STROKE_WIDTH}
                    min="1"
                    type="number"
                    value={currentEraserValue}
                    onSubmit={changeFunction}
                />
            </div>
        );
    }
    case Modes.RESHAPE:
        return (
            <div className={classNames(props.className, styles.modeTools)}>
                <InputGroup className={classNames(styles.modDashedBorder, styles.modLabeledIconHeight)}>
                    <LabeledIconButton
                        disabled={!props.hasSelectedUncurvedPoints}
                        hideLabel={hideLabel(props.intl.locale)}
                        imgSrc={curvedPointIcon}
                        title={props.intl.formatMessage(messages.curved)}
                        onClick={props.onCurvePoints}
                    />
                    <LabeledIconButton
                        disabled={!props.hasSelectedUnpointedPoints}
                        hideLabel={hideLabel(props.intl.locale)}
                        imgSrc={straightPointIcon}
                        title={props.intl.formatMessage(messages.pointed)}
                        onClick={props.onPointPoints}
                    />
                </InputGroup>
                <InputGroup className={classNames(styles.modLabeledIconHeight)}>
                    <LabeledIconButton
                        hideLabel={hideLabel(props.intl.locale)}
                        imgSrc={deleteIcon}
                        title={props.intl.formatMessage(messages.delete)}
                        onClick={props.onDelete}
                    />
                </InputGroup>
            </div>
        );
    case Modes.BIT_SELECT:
        return (
            <div className={classNames(props.className, styles.modeTools)}>
                <InputGroup className={classNames(styles.modDashedBorder, styles.modLabeledIconHeight)}>
                    <LabeledIconButton
                        hideLabel={hideLabel(props.intl.locale)}
                        imgSrc={copyIcon}
                        title={props.intl.formatMessage(messages.copy)}
                        onClick={props.onCopyToClipboard}
                    />
                    <LabeledIconButton
                        hideLabel={hideLabel(props.intl.locale)}
                        imgSrc={cutIcon}
                        title={props.intl.formatMessage(messages.cut)}
                        onClick={props.onCutToClipboard}
                    />
                    <LabeledIconButton
                        disabled={!(props.clipboardItems.length > 0)}
                        hideLabel={hideLabel(props.intl.locale)}
                        imgSrc={pasteIcon}
                        title={props.intl.formatMessage(messages.paste)}
                        onClick={props.onPasteFromClipboard}
                    />
                </InputGroup>
                <InputGroup className={classNames(styles.modDashedBorder, styles.modLabeledIconHeight)}>
                    <LabeledIconButton
                        hideLabel={hideLabel(props.intl.locale)}
                        imgSrc={deleteIcon}
                        title={props.intl.formatMessage(messages.delete)}
                        onClick={props.onDelete}
                    />
                </InputGroup>
                <InputGroup className={classNames(styles.modDashedBorder, styles.modLabeledIconHeight)}>
                        <LabeledIconButton
                            hideLabel={props.intl.locale !== 'en'}
                            imgSrc={flipHorizontalIcon}
                            title={props.intl.formatMessage(messages.flipHorizontal)}
                            onClick={props.onFlipHorizontal}
                        />
                        <LabeledIconButton
                            hideLabel={props.intl.locale !== 'en'}
                            imgSrc={flipVerticalIcon}
                            title={props.intl.formatMessage(messages.flipVertical)}
                            onClick={props.onFlipVertical}
                        />
                        <LabeledIconButton
                            hideLabel={props.intl.locale !== 'en'}
                            imgSrc={centerIcon}
                            title={props.intl.formatMessage(messages.center)}
                            onClick={props.onCenterSelection}
                        />
                </InputGroup>
            </div>
        );
    case Modes.SELECT:
        return (
            <div className={classNames(props.className, styles.modeTools)}>
                <InputGroup className={classNames(styles.modDashedBorder, styles.modLabeledIconHeight)}>
                    <LabeledIconButton
                        hideLabel={hideLabel(props.intl.locale)}
                        imgSrc={copyIcon}
                        title={props.intl.formatMessage(messages.copy)}
                        onClick={props.onCopyToClipboard}
                    />
                    {!props.noCutButton && (
                        <LabeledIconButton
                            hideLabel={hideLabel(props.intl.locale)}
                            imgSrc={cutIcon}
                            title={props.intl.formatMessage(messages.cut)}
                            onClick={props.onCutToClipboard}
                        />
                    )}
                    <LabeledIconButton
                        disabled={!(props.clipboardItems.length > 0)}
                        hideLabel={hideLabel(props.intl.locale)}
                        imgSrc={pasteIcon}
                        title={props.intl.formatMessage(messages.paste)}
                        onClick={props.onPasteFromClipboard}
                    />
                </InputGroup>
                <InputGroup className={classNames(styles.modDashedBorder, styles.modLabeledIconHeight)}>
                    <LabeledIconButton
                        hideLabel={hideLabel(props.intl.locale)}
                        imgSrc={deleteIcon}
                        title={props.intl.formatMessage(messages.delete)}
                        onClick={props.onDelete}
                    />
                </InputGroup>
                <MediaQuery minWidth={layout.fullSizeEditorMinWidth}>
                    <InputGroup className={classNames(styles.modDashedBorder)}>
                        <LabeledIconButton
                            hideLabel={props.intl.locale !== 'en'}
                            imgSrc={flipHorizontalIcon}
                            title={props.intl.formatMessage(messages.flipHorizontal)}
                            onClick={props.onFlipHorizontal}
                        />
                        <LabeledIconButton
                            hideLabel={props.intl.locale !== 'en'}
                            imgSrc={flipVerticalIcon}
                            title={props.intl.formatMessage(messages.flipVertical)}
                            onClick={props.onFlipVertical}
                        />
                        <LabeledIconButton
                            hideLabel={props.intl.locale !== 'en'}
                            imgSrc={centerIcon}
                            title={props.intl.formatMessage(messages.center)}
                            onClick={props.onCenterSelection}
                        />
                    </InputGroup>
                </MediaQuery>
                <MediaQuery minWidth={layout.fullSizeEditorMinWidth}>
                    <InputGroup>
                        <Dropdown
                            className={styles.modUnselect}
                            enterExitTransitionDurationMs={20}
                            popoverContent={
                                <InputGroup
                                    className={styles.modContextMenu}
                                >
                                    <Button
                                        className={styles.modMenuItem}
                                        onClick={props.onMask}
                                    >
                                        <TWRenderRecoloredImage
                                            className={styles.menuItemIcon}
                                            draggable={false}
                                            src={maskIcon}
                                        />
                                        <span>{props.intl.formatMessage(messages.mask)}</span>
                                    </Button>
                                    <Button
                                        className={styles.modMenuItem}
                                        onClick={props.onFilter}
                                    >
                                        <TWRenderRecoloredImage
                                            className={styles.menuItemIcon}
                                            draggable={false}
                                            src={filterIcon}
                                        />
                                        <span>{props.intl.formatMessage(messages.filter)}</span>
                                    </Button>
                                    <Button
                                        className={styles.modMenuItem}
                                        onClick={props.onSubtract}
                                    >
                                        <TWRenderRecoloredImage
                                            className={styles.menuItemIcon}
                                            draggable={false}
                                            src={subtractIcon}
                                        />
                                        <span>{props.intl.formatMessage(messages.subtract)}</span>
                                    </Button>
                                    <Button
                                        className={styles.modMenuItem}
                                        onClick={props.onMerge}
                                    >
                                        <TWRenderRecoloredImage
                                            className={styles.menuItemIcon}
                                            draggable={false}
                                            src={mergeIcon}
                                        />
                                        <span>{props.intl.formatMessage(messages.merge)}</span>
                                    </Button>
                                </InputGroup>
                            }
                            tipSize={.01}
                        >
                            {props.intl.formatMessage(messages.masktools)}
                        </Dropdown>
                    </InputGroup>
                </MediaQuery>
                <MediaQuery maxWidth={layout.fullSizeEditorMinWidth - 1}>
                    <InputGroup>
                        <Dropdown
                            className={styles.modUnselect}
                            enterExitTransitionDurationMs={20}
                            popoverContent={
                                <InputGroup
                                    className={styles.modContextMenu}
                                >
                                    <Button
                                        className={styles.modMenuItem}
                                        onClick={props.onFlipHorizontal}
                                    >
                                        <TWRenderRecoloredImage
                                            className={styles.menuItemIcon}
                                            draggable={false}
                                            src={flipHorizontalIcon}
                                        />
                                        <span>{props.intl.formatMessage(messages.flipHorizontal)}</span>
                                    </Button>
                                    <Button
                                        className={styles.modMenuItem}
                                        onClick={props.onFlipVertical}
                                    >
                                        <TWRenderRecoloredImage
                                            className={styles.menuItemIcon}
                                            draggable={false}
                                            src={flipVerticalIcon}
                                        />
                                        <span>{props.intl.formatMessage(messages.flipVertical)}</span>
                                    </Button>
                                    <Button
                                        className={styles.modMenuItem}
                                        onClick={props.onCenterSelection}
                                    >
                                        <TWRenderRecoloredImage
                                            className={styles.menuItemIcon}
                                            draggable={false}
                                            src={centerIcon}
                                        />
                                        <span>{props.intl.formatMessage(messages.center)}</span>
                                    </Button>
                                    <Button
                                        className={styles.modMenuItem}
                                        onClick={props.onMask}
                                    >
                                        <TWRenderRecoloredImage
                                            className={styles.menuItemIcon}
                                            draggable={false}
                                            src={maskIcon}
                                        />
                                        <span>{props.intl.formatMessage(messages.mask)}</span>
                                    </Button>
                                    <Button
                                        className={styles.modMenuItem}
                                        onClick={props.onFilter}
                                    >
                                        <TWRenderRecoloredImage
                                            className={styles.menuItemIcon}
                                            draggable={false}
                                            src={filterIcon}
                                        />
                                        <span>{props.intl.formatMessage(messages.filter)}</span>
                                    </Button>
                                    <Button
                                        className={styles.modMenuItem}
                                        onClick={props.onSubtract}
                                    >
                                        <TWRenderRecoloredImage
                                            className={styles.menuItemIcon}
                                            draggable={false}
                                            src={subtractIcon}
                                        />
                                        <span>{props.intl.formatMessage(messages.subtract)}</span>
                                    </Button>
                                    <Button
                                        className={styles.modMenuItem}
                                        onClick={props.onMerge}
                                    >
                                        <TWRenderRecoloredImage
                                            className={styles.menuItemIcon}
                                            draggable={false}
                                            src={mergeIcon}
                                        />
                                        <span>{props.intl.formatMessage(messages.merge)}</span>
                                    </Button>
                                </InputGroup>
                            }
                            tipSize={.01}
                        >
                            {props.intl.formatMessage(messages.more)}
                        </Dropdown>
                    </InputGroup>
                </MediaQuery>
            </div>
        );
    case Modes.BIT_TEXT:
        /* falls through */
    case Modes.TEXT:
        return (
            <div className={classNames(props.className, styles.modeTools)}>
                <InputGroup className={styles.modDashedBorder}>
                    <FontDropdown
                        onUpdateImage={props.onUpdateImage}
                        onManageFonts={props.onManageFonts}
                    />
                </InputGroup>
                <InputGroup className={styles.textAlignmentTools}>
                    <LabeledIconButton
                        gray
                        highlighted={props.textAlignment === 'left'}
                        imgSrc={alignLeftIcon}
                        title={props.intl.formatMessage(messages.alignLeft)}
                        onClick={props.onAlignLeft}
                    />
                    <LabeledIconButton
                        gray
                        highlighted={props.textAlignment === 'center'}
                        imgSrc={alignCenterIcon}
                        title={props.intl.formatMessage(messages.alignCenter)}
                        onClick={props.onAlignCenter}
                    />
                    <LabeledIconButton
                        gray
                        highlighted={props.textAlignment === 'right'}
                        imgSrc={alignRightIcon}
                        title={props.intl.formatMessage(messages.alignRight)}
                        onClick={props.onAlignRight}
                    />
                    <LabeledIconButton
                        gray
                        highlighted={props.textAlignment === 'justify'}
                        imgSrc={alignJustifyIcon}
                        title={props.intl.formatMessage(messages.alignJustify)}
                        onClick={props.onAlignJustify}
                    />
                </InputGroup>
            </div>
        );
    case Modes.BIT_RECT:
        /* falls through */
    case Modes.BIT_OVAL:
    {
        const fillIcon = props.mode === Modes.BIT_RECT ? bitRectIcon : bitOvalIcon;
        const outlineIcon = props.mode === Modes.BIT_RECT ? bitRectOutlinedIcon : bitOvalOutlinedIcon;
        return (
            <div className={classNames(props.className, styles.modeTools)}>
                <InputGroup>
                    <LabeledIconButton
                        highlighted={props.fillBitmapShapes}
                        imgSrc={fillIcon}
                        title={props.intl.formatMessage(messages.filled)}
                        onClick={props.onFillShapes}
                        gray
                    />
                </InputGroup>
                <InputGroup>
                    <LabeledIconButton
                        highlighted={!props.fillBitmapShapes}
                        imgSrc={outlineIcon}
                        title={props.intl.formatMessage(messages.outlined)}
                        onClick={props.onOutlineShapes}
                        gray
                    />
                </InputGroup>
                {props.fillBitmapShapes ? null : (
                    <InputGroup>
                        <Label text={props.intl.formatMessage(messages.thickness)}>
                            <LiveInput
                                range
                                small
                                max={MAX_STROKE_WIDTH}
                                min="1"
                                type="number"
                                value={props.bitBrushSize}
                                onSubmit={props.onBitBrushSliderChange}
                            />
                        </Label>
                    </InputGroup>)
                }
            </div>
        );
    }
    case Modes.POLYGON: {
        return (
            <div className={classNames(props.className, styles.modeTools)}>
                <InputGroup>
                    <img
                        alt={props.intl.formatMessage(messages.polygonSides)}
                        className={styles.polygonSidesIcon}
                        draggable={false}
                        src={pentagonIcon}
                    />
                    <LiveInput
                        range
                        small
                        max={MAX_POLYGON_SIDES}
                        min={MIN_POLYGON_SIDES}
                        type="number"
                        value={props.polygonSides}
                        onSubmit={props.onPolygonSidesChange}
                    />
                </InputGroup>
                <InputGroup>
                    <img
                        alt={props.intl.formatMessage(messages.polygonRadius)}
                        className={styles.modeToolsIcon}
                        draggable={false}
                        src={roundedCornerIcon}
                    />
                    <LiveInput
                        range
                        small
                        max={MAX_STROKE_WIDTH}
                        min="0"
                        type="number"
                        value={props.polygonRadius}
                        onSubmit={props.onPolygonRadiusChange}
                    />
                </InputGroup>
            </div>
        );
    }
    case Modes.BIT_EFFECTS:
        /* falls through */
    case Modes.EFFECTS:
        return (
            <div className={classNames(props.className, styles.modeTools)}>
                <InputGroup className={styles.modDashedBorder}>
                    <Dropdown
                        className={styles.modUnselect}
                        enterExitTransitionDurationMs={20}
                        popoverContent={
                            <InputGroup className={styles.modContextMenu}>
                                <EffectOption
                                    id={'hueShift'}
                                    icon={effectsHueShiftIcon}
                                    message={'HueShift'}
                                />
                                <EffectOption
                                    id={'brightness'}
                                    icon={effectsBrightnessIcon}
                                    message={'Brightness'}
                                />
                                <EffectOption
                                    id={'saturate'}
                                    icon={effectsSaturateIcon}
                                    message={'Saturate'}
                                />
                                <EffectOption
                                    id={'opacity'}
                                    icon={effectsOpacityIcon}
                                    message={'Opacity'}
                                />
                                <EffectOption
                                    id={'posterize'}
                                    icon={effectsPosterizeIcon}
                                    message={'Posterize'}
                                />
                            </InputGroup>
                        }
                        tipSize={.01}
                        invisible
                    >
                        <LabeledIconButton
                            hideLabel={hideLabel(props.intl.locale)}
                            imgSrc={effectsColorIcon}
                            title={props.intl.formatMessage(messages.effectsColor)}
                        />
                    </Dropdown>
                </InputGroup>
                {isVector(props.format) && blendModeControl}
                <InputGroup className={classNames(styles.modLabeledIconHeight)}>
                    <LabeledIconButton
                        hideLabel={hideLabel(props.intl.locale)}
                        imgSrc={deleteIcon}
                        title={props.intl.formatMessage(messages.delete)}
                        onClick={props.onDelete}
                    />
                </InputGroup>
            </div>
        );
    default:
        // Leave empty for now, if mode not supported
        return (
            <div className={classNames(props.className, styles.modeTools)} />
        );
    }
};

ModeToolsComponent.propTypes = {
    noCutButton: PropTypes.bool,
    bitBrushSize: PropTypes.number,
    bitEraserSize: PropTypes.number,
    brushValue: PropTypes.number,
    className: PropTypes.string,
    clipboardItems: PropTypes.arrayOf(PropTypes.array),
    eraserValue: PropTypes.number,
    fillBitmapShapes: PropTypes.bool,
    format: PropTypes.oneOf(Object.keys(Formats)),
    hasSelectedUncurvedPoints: PropTypes.bool,
    hasSelectedUnpointedPoints: PropTypes.bool,
    intl: intlShape.isRequired,
    mode: PropTypes.string.isRequired,
    onBitBrushSliderChange: PropTypes.func.isRequired,
    onBitEraserSliderChange: PropTypes.func.isRequired,
    onBrushSliderChange: PropTypes.func.isRequired,
    onCopyToClipboard: PropTypes.func.isRequired,
    onCutToClipboard: PropTypes.func.isRequired,
    onCurvePoints: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onEraserSliderChange: PropTypes.func,
    onFillShapes: PropTypes.func.isRequired,
    onFlipHorizontal: PropTypes.func.isRequired,
    onFlipVertical: PropTypes.func.isRequired,
    onCenterSelection: PropTypes.func.isRequired,
    onManageFonts: PropTypes.func,
    onOutlineShapes: PropTypes.func.isRequired,
    onPasteFromClipboard: PropTypes.func.isRequired,
    onPointPoints: PropTypes.func.isRequired,
    onPolygonSidesChange: PropTypes.func.isRequired,
    onUpdateImage: PropTypes.func.isRequired,
    onMask: PropTypes.func,
    onFilter: PropTypes.func,
    onSubtract: PropTypes.func,
    onMerge: PropTypes.func,
    polygonRadius: PropTypes.number,
    polygonSides: PropTypes.number,
    textAlignment: PropTypes.string.isRequired,
    onAlignLeft: PropTypes.func.isRequired,
    onAlignCenter: PropTypes.func.isRequired,
    onAlignRight: PropTypes.func.isRequired,
    onAlignJustify: PropTypes.func.isRequired,
    onRectRadiusSliderChange: PropTypes.func,
    // eslint-disable-next-line react/no-unused-prop-types
    onApplyEffect: PropTypes.func.isRequired,
    blendMode: PropTypes.string,
    hasSelection: PropTypes.bool,
    onChangeBlendMode: PropTypes.func,
    onPolygonRadiusChange: PropTypes.func
};

const mapStateToProps = state => ({
    mode: state.scratchPaint.mode,
    format: state.scratchPaint.format,
    fillBitmapShapes: state.scratchPaint.fillBitmapShapes,
    bitBrushSize: state.scratchPaint.bitBrushSize,
    bitEraserSize: state.scratchPaint.bitEraserSize,
    brushValue: state.scratchPaint.brushMode.brushSize,
    clipboardItems: state.scratchPaint.clipboard.items,
    eraserValue: state.scratchPaint.eraserMode.brushSize,
    polygonRadius: state.scratchPaint.polygonMode.polygonRadius,
    polygonSides: state.scratchPaint.polygonMode.polygonSides,
    textAlignment: state.scratchPaint.textAlignment
});
const mapDispatchToProps = dispatch => ({
    onAlignLeft: () => {
        dispatch(changeTextAlignment('left'));
    },
    onAlignCenter: () => {
        dispatch(changeTextAlignment('center'));
    },
    onAlignRight: () => {
        dispatch(changeTextAlignment('right'));
    },
    onAlignJustify: () => {
        dispatch(changeTextAlignment('justify'));
    },
    onBrushSliderChange: brushSize => {
        dispatch(changeBrushSize(brushSize));
    },
    onBitBrushSliderChange: bitBrushSize => {
        dispatch(changeBitBrushSize(bitBrushSize));
    },
    onBitEraserSliderChange: eraserSize => {
        dispatch(changeBitEraserSize(eraserSize));
    },
    onEraserSliderChange: eraserSize => {
        dispatch(changeEraserSize(eraserSize));
    },
    onFillShapes: () => {
        dispatch(setShapesFilled(true));
    },
    onOutlineShapes: () => {
        dispatch(setShapesFilled(false));
    },
    onPolygonRadiusChange: polygonRadius => {
        dispatch(changePolygonRadius(polygonRadius));
    },
    onPolygonSidesChange: polygonSides => {
        dispatch(changePolygonSides(polygonSides));
    }
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(injectIntl(ModeToolsComponent));
